import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import {
  ApPositionSummaryPayload,
  ApPositionSummaryItem,
  FinancialReportPeriodPreset
} from '../models/financial-report.models';
import { FINANCIAL_REPORT_PERIOD_OPTIONS } from '../models/financial-report.models';

function formatCurrencyBr(value: number | undefined | null): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
}

function formatDateBr(iso: string | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('T')[0].split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function periodLabel(preset: FinancialReportPeriodPreset): string {
  return FINANCIAL_REPORT_PERIOD_OPTIONS.find((o) => o.value === preset)?.label ?? preset;
}

function itemRows(items: ApPositionSummaryItem[] | undefined): (string | number)[][] {
  return (items ?? []).map((i) => [
    i.documentNumber ?? '—',
    i.documentType ?? '—',
    i.status ?? '—',
    i.category ?? '—',
    formatDateBr(i.dueDate),
    i.supplierName ?? '—',
    i.costCenterName ?? '—',
    i.agingLabel ?? '—',
    Number(i.openAmount) || 0
  ]);
}

const ITEM_HEADERS = [
  'Documento',
  'Tipo',
  'Status',
  'Categoria',
  'Vencimento',
  'Fornecedor',
  'Centro de custo',
  'Aging',
  'Valor em aberto (R$)'
];

function writeWorkbook(fileName: string, sheetData: (string | number)[][]): void {
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Posição CP');
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const safeName = fileName.replace(/\.(csv|xls|xlsx)$/i, '');
  saveAs(blob, `${safeName}.xlsx`);
}

/** Exporta o relatório de posição CP para Excel (.xlsx). */
export function exportApPositionSummaryToExcel(
  summary: ApPositionSummaryPayload,
  generatedAtUtc: string
): void {
  const stamp = new Date().toISOString().split('T')[0];
  const sheetData: (string | number)[][] = [
    [summary.reportTitle || 'Posição de contas a pagar'],
    [`Período: ${summary.periodDescription || periodLabel(summary.periodPreset)}`],
    [
      `Pagamentos: ${formatDateBr(summary.paymentPeriodFrom)} a ${formatDateBr(summary.paymentPeriodTo)}`
    ],
    [`Referência: ${formatDateBr(summary.referenceDate)}`],
    [`Gerado em: ${formatDateBr(generatedAtUtc)}`],
    [
      `Pendentes: ${summary.pendingCount} — ${formatCurrencyBr(summary.pendingAmount)} | Vencidos: ${summary.overdueCount} — ${formatCurrencyBr(summary.overdueAmount)} | Pagos: ${summary.paidInPeriodCount} — ${formatCurrencyBr(summary.paidInPeriodAmount)}`
    ],
    [],
    ['Aging em aberto'],
    ['Faixa', 'Quantidade', 'Valor (R$)'],
    ...(summary.openAging?.buckets ?? []).map((b) => [b.label, b.count, Number(b.amount) || 0]),
    [
      'Total',
      summary.openAging?.totalCount ?? 0,
      Number(summary.openAging?.totalAmount) || 0
    ],
    [],
    ['Por categoria (em aberto)'],
    ['Categoria', 'Quantidade', 'Valor (R$)'],
    ...(summary.openByCategory ?? []).map((r) => [r.key, r.count, Number(r.amount) || 0]),
    [],
    ['Títulos pendentes'],
    ITEM_HEADERS,
    ...itemRows(summary.pending?.items),
    [],
    ['Títulos vencidos'],
    ITEM_HEADERS,
    ...itemRows(summary.overdue?.items),
    [],
    ['Pagos no período'],
    ITEM_HEADERS,
    ...itemRows(summary.paidInPeriod?.items)
  ];

  writeWorkbook(`posicao_cp_${stamp}`, sheetData);
}

/** Exporta o relatório de posição CP para PDF. */
export function exportApPositionSummaryToPdf(
  summary: ApPositionSummaryPayload,
  generatedAtUtc: string
): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const margin = 14;
  let y = margin;
  const pageW = doc.internal.pageSize.getWidth();
  const maxW = pageW - margin * 2;

  const addLine = (text: string, size = 10, bold = false) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(text, maxW);
    for (const line of lines) {
      if (y > 280) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += size * 0.45 + 2;
    }
  };

  addLine(summary.reportTitle || 'Posição de contas a pagar', 14, true);
  addLine(`Período: ${summary.periodDescription || periodLabel(summary.periodPreset)}`, 10);
  addLine(
    `Pagamentos: ${formatDateBr(summary.paymentPeriodFrom)} a ${formatDateBr(summary.paymentPeriodTo)}`,
    9
  );
  addLine(`Gerado em: ${formatDateBr(generatedAtUtc)}`, 9);
  y += 2;

  addLine('Indicadores', 11, true);
  addLine(`Pendentes: ${summary.pendingCount} títulos — ${formatCurrencyBr(summary.pendingAmount)}`, 9);
  addLine(`Vencidos: ${summary.overdueCount} títulos — ${formatCurrencyBr(summary.overdueAmount)}`, 9);
  addLine(
    `Pagos no período: ${summary.paidInPeriodCount} títulos — ${formatCurrencyBr(summary.paidInPeriodAmount)}`,
    9
  );
  y += 2;

  addLine('Aging em aberto', 11, true);
  for (const b of summary.openAging?.buckets ?? []) {
    addLine(`${b.label}: ${b.count} — ${formatCurrencyBr(b.amount)}`, 9);
  }
  addLine(
    `Total em aberto: ${summary.openAging?.totalCount ?? 0} — ${formatCurrencyBr(summary.openAging?.totalAmount)}`,
    9,
    true
  );
  y += 2;

  const addItemSection = (title: string, items: ApPositionSummaryItem[] | undefined, max = 25) => {
    const list = items ?? [];
    if (!list.length) return;
    addLine(title, 11, true);
    const slice = list.slice(0, max);
    for (const i of slice) {
      addLine(
        `${i.documentNumber ?? '—'} | ${formatDateBr(i.dueDate)} | ${i.supplierName ?? '—'} | ${formatCurrencyBr(i.openAmount)}`,
        8
      );
    }
    if (list.length > max) {
      addLine(`… e mais ${list.length - max} registro(s). Exporte o Excel para a lista completa.`, 8);
    }
    y += 2;
  };

  addItemSection('Pendentes (amostra)', summary.pending?.items);
  addItemSection('Vencidos (amostra)', summary.overdue?.items);

  const stamp = new Date().toISOString().split('T')[0];
  doc.save(`posicao_cp_${stamp}.pdf`);
}

/** Exporta linhas tabulares do espelho financeiro para PDF. */
export function exportMirrorTableToPdf(
  title: string,
  metaLines: string[],
  headers: string[],
  rows: (string | number)[][],
  fileName: string
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const margin = 10;
  let y = margin;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, y);
  y += 7;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  for (const line of metaLines) {
    doc.text(line, margin, y);
    y += 4;
  }
  y += 3;

  const colCount = headers.length;
  const pageW = doc.internal.pageSize.getWidth();
  const tableW = pageW - margin * 2;
  const colW = tableW / colCount;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  headers.forEach((h, i) => {
    doc.text(String(h).slice(0, 18), margin + i * colW, y);
  });
  y += 5;
  doc.setFont('helvetica', 'normal');

  const maxRows = 80;
  const exportRows = rows.slice(0, maxRows);
  for (const row of exportRows) {
    if (y > 190) {
      doc.addPage();
      y = margin;
    }
    row.forEach((cell, i) => {
      doc.text(String(cell ?? '').slice(0, 22), margin + i * colW, y);
    });
    y += 4;
  }

  if (rows.length > maxRows) {
    y += 4;
    doc.text(`… ${rows.length - maxRows} registro(s) omitidos. Use Excel para exportação completa.`, margin, y);
  }

  const stamp = new Date().toISOString().split('T')[0];
  doc.save(`${fileName}_${stamp}.pdf`);
}
